import React, { useEffect, useRef, useState } from 'react';
import PageLayout from '../components/PageLayout';
import SEO from '../components/SEO';
import useVisibility from 'react-use-visibility';
import { useStaticQuery, graphql, Link } from 'gatsby';
import { GatsbySanityImageFluid } from '../types';
import { replaceNewLinesWithBr } from '../utils/sanity';
import { useTranslation } from 'react-i18next';
import halfTree from '../assets/images/graphic_elements/half-tree.svg';
import Img from 'gatsby-image/withIEPolyfill';
import { COUNTRY_TO_CAREER_LINK, COUNTRY_CODE } from '../constants';

import * as styles from './careers.module.scss';
import GenericLink from '../components/GenericLink';

interface TextAndImage {
  image: {
    asset: {
      fluid: GatsbySanityImageFluid;
    };
  };
  title: string;
  text: string;
}

interface QueryData {
  sanityCareers: {
    pageTitle: string;
    textAndImages: Array<TextAndImage>;
    featuredText: {
      title: string;
      text: string;
    };
  };
}

const Careers = (): React.ReactElement => {
  const data: QueryData = useStaticQuery(graphql`
    {
      sanityCareers {
        pageTitle
        textAndImages {
          image {
            asset {
              fluid(maxWidth: 1920) {
                ...GatsbySanityImageFluid
              }
            }
          }
          title
          text
        }
        featuredText {
          title
          text
        }
      }
    }
  `);

  const { t } = useTranslation();
  const careers = data.sanityCareers;
  const [readyToShowFloatingApplyBtn, setReadyToShowFloatingApplyBtn] = useState<boolean>(false);
  const [staticApplyBtnRef, setStaticApplyBtnRef] = useState<HTMLDivElement | null>(null);

  const isStaticApplyBtnVisible =
    typeof window !== 'undefined' && useVisibility(staticApplyBtnRef, { partial: true });

  useEffect(() => {
    setTimeout(() => {
      setReadyToShowFloatingApplyBtn(true);
    }, 1000);
  }, []);

  const careerCountryLink =
    COUNTRY_TO_CAREER_LINK[COUNTRY_CODE as keyof typeof COUNTRY_TO_CAREER_LINK];

  return (
    careers && (
      <PageLayout title={careers.pageTitle} withPageContentFullWidth withoutCircles={true}>
        <SEO />
        <div className={styles.container}>
          {careers.textAndImages &&
            careers.textAndImages.map((textAndImage, i) => (
              <div className={styles.textAndImageContainer} key={i}>
                <div className={styles.imageContainer}>
                  <Img className={styles.image} fluid={textAndImage.image.asset.fluid} alt="" />
                </div>
                <div className={styles.textContainer}>
                  <h3 className={styles.title}>{textAndImage.title}</h3>
                  <p className={styles.text}>{replaceNewLinesWithBr(textAndImage.text)}</p>
                </div>
              </div>
            ))}
          <div
            className={styles.featuredTextContainer}
            ref={newRef => setStaticApplyBtnRef(newRef)}
          >
            <div className={styles.featuredContent}>
              <h3 className={styles.featuredTitle}>{careers.featuredText.title}</h3>
              <p className={styles.featuredText}>{careers.featuredText.text}</p>
              <GenericLink className={styles.button} to={careerCountryLink} noExternalUrlIcon>
                {t('careers_page.apply_button', 'Apply now!')}
              </GenericLink>
            </div>
            <div className={styles.halfTreeContainer}>
              <img src={halfTree} alt="" className={styles.halfTree} />
            </div>
          </div>
          {careerCountryLink && (
            <GenericLink
              className={
                styles.button +
                ' ' +
                styles.floatingApplyButton +
                ' ' +
                (readyToShowFloatingApplyBtn &&
                  !isStaticApplyBtnVisible &&
                  styles.floatingApplyButtonVisible)
              }
              to={careerCountryLink}
              noExternalUrlIcon
            >
              {t('careers_page.apply_button', 'Apply now!')}
            </GenericLink>
          )}
        </div>
      </PageLayout>
    )
  );
};
export default Careers;
